import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgStore } from '@/stores/orgStore'
import { policiesApi } from '@/api/clients/policiesApi'
import { PageHeader, Card, Button, Field, TextInput, TextArea } from '@/components/common/ui'
import { Badge } from '@/components/common/Badge'
import { Modal } from '@/components/common/Modal'
import { QueryState } from '@/components/common/State'
import { RoleGate } from '@/components/common/RoleGate'
import { formatDate } from '@/components/common/format'

export default function Policies() {
  const { currentOrgId: orgId } = useOrgStore()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [showDeadlines, setShowDeadlines] = useState(false)
  const [showImport, setShowImport] = useState(false)

  const policies = useQuery({
    queryKey: ['policies', orgId],
    queryFn: () => policiesApi.list(orgId),
    enabled: !!orgId,
  })

  return (
    <div>
      <PageHeader
        title="Policies"
        subtitle="Deterministic CEL rule engine — the source of truth for scoring"
        actions={
          <>
            <Button icon="event" onClick={() => setShowDeadlines(true)}>
              Deadlines
            </Button>
            <RoleGate required="security_owner">
              <Button icon="upload" onClick={() => setShowImport(true)}>
                Import
              </Button>
            </RoleGate>
            <RoleGate required={['policy_author', 'security_owner']}>
              <Button variant="primary" icon="add" onClick={() => setShowCreate(true)}>
                New policy
              </Button>
            </RoleGate>
          </>
        }
      />

      <Card className="!p-0">
        <QueryState
          isLoading={policies.isLoading}
          error={policies.error}
          isEmpty={policies.data?.length === 0}
          emptyProps={{ icon: 'gavel', title: 'No policies' }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-subtle-light dark:text-subtle-dark border-b border-border-light dark:border-border-dark">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Engine</th>
                <th className="px-5 py-3 font-medium">Strategy</th>
                <th className="px-5 py-3 font-medium">Rules</th>
                <th className="px-5 py-3 font-medium">Version</th>
                <th className="px-5 py-3 font-medium">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light dark:divide-border-dark">
              {policies.data?.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => navigate(`/policies/${p.id}`)}
                  className="cursor-pointer hover:bg-primary/5"
                >
                  <td className="px-5 py-3 font-medium text-text-light dark:text-text-dark">
                    {p.name}
                    {p.is_shipped_default && (
                      <span className="ml-2">
                        <Badge label="shipped" variant="neutral" />
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <Badge label={p.engine_type} variant="info" />
                  </td>
                  <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark">{p.strategy}</td>
                  <td className="px-5 py-3">{p.rules?.length ?? 0}</td>
                  <td className="px-5 py-3">v{p.version}</td>
                  <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark">
                    {formatDate(p.updated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </QueryState>
      </Card>

      {showCreate && (
        <CreatePolicyModal
          orgId={orgId}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ['policies', orgId] })
            setShowCreate(false)
            navigate(`/policies/${id}`)
          }}
        />
      )}
      {showDeadlines && <DeadlinesModal orgId={orgId} onClose={() => setShowDeadlines(false)} />}
      {showImport && (
        <ImportPolicyModal
          orgId={orgId}
          onClose={() => setShowImport(false)}
          onImported={() => {
            qc.invalidateQueries({ queryKey: ['policies', orgId] })
            setShowImport(false)
          }}
        />
      )}
    </div>
  )
}

function ImportPolicyModal({
  orgId,
  onClose,
  onImported,
}: {
  orgId: string
  onClose: () => void
  onImported: () => void
}) {
  const [pkg, setPkg] = useState('')
  const [rationale, setRationale] = useState('')
  const [err, setErr] = useState('')
  const mut = useMutation({
    mutationFn: () => {
      let parsed: unknown
      try {
        parsed = JSON.parse(pkg)
      } catch {
        throw new Error('package must be valid JSON')
      }
      return policiesApi.import(orgId, { ...(parsed as object), rationale })
    },
    onSuccess: onImported,
    onError: (e) => setErr((e as Error).message),
  })
  return (
    <Modal
      title="Import signed policy"
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!pkg || rationale.length < 20 || mut.isPending} onClick={() => mut.mutate()}>
            Import
          </Button>
        </>
      }
    >
      <p className="text-sm text-subtle-light dark:text-subtle-dark mb-3">
        Paste a sigstore/cosign-signed policy package (JSON). Unsigned packages are rejected
        server-side; Security Owner approval required (§5.3.10).
      </p>
      <div className="space-y-4">
        <Field label="Signed package (JSON)">
          <TextArea rows={8} value={pkg} onChange={(e) => setPkg(e.target.value)} />
        </Field>
        <Field label="Rationale (audit)">
          <TextInput value={rationale} onChange={(e) => setRationale(e.target.value)} />
        </Field>
        {(err || mut.error) && <p className="text-sm text-error">{err || (mut.error as Error).message}</p>}
      </div>
    </Modal>
  )
}

function CreatePolicyModal({
  orgId,
  onClose,
  onCreated,
}: {
  orgId: string
  onClose: () => void
  onCreated: (id: string) => void
}) {
  const [name, setName] = useState('')
  const [rationale, setRationale] = useState('')
  const mut = useMutation({
    mutationFn: () =>
      policiesApi.create(orgId, {
        name,
        engine_type: 'rule',
        strategy: 'most_severe',
        rules: [],
        rationale,
      }),
    onSuccess: (p) => onCreated(p.id),
  })
  return (
    <Modal
      title="New policy"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!name || rationale.length < 20 || mut.isPending} onClick={() => mut.mutate()}>
            Create
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Rationale (audit)">
          <TextInput value={rationale} onChange={(e) => setRationale(e.target.value)} />
        </Field>
        {mut.error && <p className="text-sm text-error">{(mut.error as Error).message}</p>}
      </div>
    </Modal>
  )
}

function DeadlinesModal({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const deadlines = useQuery({
    queryKey: ['deadlines', orgId],
    queryFn: () => policiesApi.deadlines(orgId),
    enabled: !!orgId,
  })
  const [date, setDate] = useState('')
  const project = useMutation({
    mutationFn: () => policiesApi.simulateDeadline(orgId, date),
  })
  return (
    <Modal title="Regulatory deadlines" onClose={onClose} wide>
      <QueryState
        isLoading={deadlines.isLoading}
        error={deadlines.error}
        isEmpty={deadlines.data?.length === 0}
        emptyProps={{ icon: 'event_available', title: 'No upcoming deadlines' }}
      >
        <ul className="divide-y divide-border-light dark:divide-border-dark">
          {deadlines.data?.map((d, i) => (
            <li key={i} className="flex items-center justify-between py-2 text-sm">
              <span className="font-medium text-text-light dark:text-text-dark">{d.framework}</span>
              <span className="text-subtle-light dark:text-subtle-dark">
                {formatDate(d.deadline)} · {d.days_remaining}d · {d.affected_assets} assets
              </span>
            </li>
          ))}
        </ul>
      </QueryState>

      {/* Projection — what moves band if a deadline lands on a chosen date (§5.3.12) */}
      <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark">
        <p className="label-sm text-subtle-light dark:text-subtle-dark mb-2">Project deadline impact</p>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Field label="Deadline date">
              <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
          </div>
          <Button icon="insights" disabled={!date || project.isPending} onClick={() => project.mutate()}>
            Project
          </Button>
        </div>
        {project.error && <p className="mt-2 text-sm text-error">{(project.error as Error).message}</p>}
        {project.data != null && (
          <pre className="mt-2 p-3 rounded-xl bg-container-light dark:bg-container-dark text-xs overflow-x-auto text-text-light dark:text-text-dark">
            {JSON.stringify(project.data, null, 2)}
          </pre>
        )}
      </div>
    </Modal>
  )
}
